import { Epic, ofType } from 'redux-observable';
import { of, concat, EMPTY, from } from 'rxjs';
import { mergeMap, catchError, tap, switchMap, map } from 'rxjs/operators';
import { push } from 'connected-react-router';

import { IAction, IRootState, IEpicDependencies } from '../rootState';
import { actions, ActionType } from './actions';
import { coreState } from '../core';
import { generalState } from '../general';
import { GENERAL } from '../../../constants';
import { clientState } from '../client';
import { UserModel } from 'modules/models';
import { ToastType } from 'modules/models/general';

const config = process.env;

export const authSignIn: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SIGN_IN_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, true)),
        deps.authService.signIn(payload || {}).pipe(
          mergeMap(res => {
            const actionsCollection: IAction[] = [];
            actionsCollection.push(actions.setClientId(null));
            actionsCollection.push(actions.setOriginalCompanyId(null));
            if (res && res.pool && res.pool.clientId) {
              actionsCollection.push(actions.setClientId(res.pool.clientId));
              if (res?.signInUserSession?.idToken?.payload && res?.signInUserSession?.idToken?.payload['custom:currentcompanyid']) {
                actionsCollection.push(actions.setOriginalCompanyId(res.signInUserSession.idToken.payload['custom:currentcompanyid']));
              }
              actionsCollection.push(actions.setUserName(res.username));
              if (!!res.username) actionsCollection.push(clientState.actions.fetchUserClientsStart(res.username, res.pool.clientId, res));
            }
            return concat(from(actionsCollection));
          })
        )
        // of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false, true, error))))
    )
  );

export const authSignOut: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SIGN_OUT_START),
    mergeMap(() =>
      concat(
        deps.authService.signOut().pipe(
          tap(() => {
            document.cookie = `session=; expires=Thu, 01 Jan 1970 00:00:00 UTC;domain=${config.REACT_APP_COOKIE_DOMAIN}`;
            window.location.reload();
          }),
          switchMap(() => EMPTY)
        )
      )
    )
  );

export const authInitiate: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.INITIATE_AUTH),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, true)),
        deps.apiService.initiateAuth(payload.userName, payload.clientId).pipe(
          mergeMap(res => {
            const actionsCollection: IAction[] = [];
            actionsCollection.push(actions.respondToChallenge(payload.userName, payload.clientId, payload.selectedCompany, res));
            return concat(from(actionsCollection));
          })
        )
        // of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false, true, error))))
    )
  );

export const authRespondToChallenge: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.RESPOND_TO_CHALLENGE_AUTH),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, true)),
        deps.apiService.respondToAuthChallenge(payload.userName, payload.clientId, payload.selectedCompany, payload.authResponse).pipe(
          mergeMap(res => {
            const actionsCollection: IAction[] = [];
            if (res && res.isSuccess) {
              const baseStorageKey = 'CognitoIdentityServiceProvider.' + payload.clientId + '.' + payload.userName + '.';
              localStorage.setItem(baseStorageKey + 'accessToken', res.authenticationResult.accessToken);
              localStorage.setItem(baseStorageKey + 'idToken', res.authenticationResult.idToken);
              localStorage.setItem(baseStorageKey + 'refreshToken', res.authenticationResult.refreshToken);
              actionsCollection.push(actions.setClientId(null));
              actionsCollection.push(actions.recoverSessionStart());
              actionsCollection.push(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false));
            } else {
              actionsCollection.push(
                generalState.actions.setLoading(
                  GENERAL.LOADING_KEY.AUTH_LOGIN,
                  false,
                  true,
                  'Failed to login to this company. Please select different company.'
                )
              );
            }
            return concat(from(actionsCollection));
          })
        )
      ).pipe(catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false, true, error))))
    )
  );

export const recoverSession: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.RECOVER_SESSION_START),
    mergeMap(() =>
      deps.authService.isSignedIn().pipe(
        mergeMap(res => {
          const session = res.signInUserSession.idToken.payload;
          localStorage.setItem('session', JSON.stringify(session));
          const isCompany = session['custom:currentcompanyid'];
          const companyUserId = session['custom:companyuserid'];
          const userInfo = {
            fullName: session['custom:fullname'],
            username: res.username,
            companyUserId: session['custom:companyuserid'],
            companyWorkerId: session['custom:companyworkerid'],
            currentCompanyId: session['custom:currentcompanyid'],
            isFcaUser: session['custom:currentcompanyid'] === 'ffffffff-ffff-ffff-ffff-ffffffffffff',
            jwtToken: res.signInUserSession.idToken.jwtToken,
            attributes: res.attributes,
            isAdmin: session['custom:isadmin'] === 'True',
            isSuperAdmin: session['custom:issuperadmin'] === 'True',
            isAdminOnAnyProject: session['custom:isadmininanyproject'] === 'True',
          };
          document.cookie = `session=; expires=Thu, 01 Jan 1970 00:00:00 UTC;domain=${config.REACT_APP_COOKIE_DOMAIN}`;
          document.cookie = `session=${JSON.stringify(userInfo)};domain=${config.REACT_APP_COOKIE_DOMAIN}`;
          const actionsCollection: IAction[] = [];
          if (!!companyUserId) actionsCollection.push(actions.fetchUserPermissionsStart(companyUserId));
          if (!!isCompany && isCompany !== 'ffffffff-ffff-ffff-ffff-ffffffffffff') actionsCollection.push(clientState.actions.fetchClientStart(isCompany));
          return concat(from(actionsCollection));
        }),
        catchError(() => of(actions.signOutSuccess(), push('/')))
      )
    )
  );

export const fetchUserPermissionsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_PERMISSIONS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PERMISSIONS, true)),
        deps.apiService.getUserPermissions(payload.id).pipe(
          mergeMap(res => {
            const hasValidPermission = res.find(({ name }) => name === 'AppAccess.Web');
            let isProjectAdmin = res?.some?.(
              ({ source, name, entityType }) => name === UserModel.AdminPermission.GENERAL && source === 'Scope' && entityType === 'Project'
            );
            const parts = document.cookie.split('session=');
            const session =
              parts.length === 2
                ? parts
                    .pop()!
                    .split(';')
                    .shift()
                : parts
                    ?.reverse()
                    ?.find(p => p?.includes?.('jwtToken'))
                    ?.split(';')
                    ?.shift();
            if (hasValidPermission) {
              const userResponse = JSON.parse((session || null) as any) || {};
              document.cookie = `session=; expires=Thu, 01 Jan 1970 00:00:00 UTC;domain=${config.REACT_APP_COOKIE_DOMAIN}`;
              document.cookie = `session=${JSON.stringify({ ...userResponse, isProjectAdmin })};domain=${config.REACT_APP_COOKIE_DOMAIN}`;
            } else {
              return concat(of(generalState.actions.addToastStart('Not authorised to access the application', ToastType.ERROR)), of(actions.signOutStart()));
            }
            let sessionData = localStorage.getItem('session') && JSON.parse(localStorage.getItem('session'));
            localStorage.removeItem('session');
            if (!sessionData) {
              const parsedSession = JSON.parse(session || null) || {};
              sessionData = {
                ...parsedSession,
                'custom:companyworkerid': parsedSession?.companyWorkerId,
                'custom:isadmin': parsedSession?.isAdmin ? 'True' : 'False',
                'custom:companyuserid': parsedSession?.companyUserId,
                email: parsedSession?.attributes?.email,
                'custom:fullname': parsedSession?.fullName,
                'custom:currentcompanyid': parsedSession?.currentCompanyId,
                'cognito:username': parsedSession?.username,
                'custom:selectedcompanyid': parsedSession?.currentCompanyId,
                // "custom:appaccess": "AppAccess.Web,AppAccess.JobSite,AppAccess.TabletExperience",
                'custom:issuperadmin': parsedSession?.isSuperAdmin ? 'True' : 'False',
                'custom:isadmininanyproject': parsedSession?.isAdminOnAnyProject ? 'True' : 'False',
              };
            }
            return concat(
              of(actions.recoverSessionDone()),
              of(actions.signInSuccess(sessionData)),
              of(actions.fetchUserPermissionsSuccess(res, isProjectAdmin))
            );
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PERMISSIONS, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PERMISSIONS, false, true, error))
        )
      )
    )
  );

export const epics = [authSignIn, authSignOut, authInitiate, authRespondToChallenge, recoverSession, fetchUserPermissionsStart];
